import cloneDeep from 'lodash/cloneDeep';

import { RESIDENCE_TYPE_LABEL } from '@webapp/account/src/resources/questions/questions';
import type { GlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import type { Comment, CommentResponse, Item, Report, Row } from '@webapp/account/src/resources/reports/reports';
import { initialCell, initialCol, initialItem, initialRow } from '@webapp/account/src/resources/reports/reports';
import type { ReportsState } from '@webapp/account/src/resources/reports/state';

import { NpsType } from '@webapp/common/lib/ui';
import { parseJSON, striptags } from '@webapp/common/lib/utils';
import { CANTRATE_ID, CANTRATE_TEXT } from '@webapp/common/resources/constants';
import { QuestionType } from '@webapp/common/resources/survey';

const getValue = (jsonTitle: string, pos = 0): string => {
    let value = '';
    try {
        value = JSON.parse(jsonTitle);
        return value[pos];
    } catch (e) {}

    return jsonTitle;
};

export const smile = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = report.question.rows.map(({ id, value, value2 }) => {
        const response = responses.find(({ ANSWER_ID }: any) => id === ANSWER_ID);
        return {
            ...cloneDeep(initialRow),
            id,
            count: response?.CNT || 0,
            value1: String(value || ''),
            value2: String(value2 || ''),
        };
    });

    report.items = [item];

    return report;
};

const agreement = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    const {
        question: { rows },
    } = report;

    // TODO ?? remove hardcode Да/Нет values
    item.rows = rows.map(({ id, value }) => {
        const resp = responses.find((response) => {
            return response.ANSWER_ID === id;
        });

        return {
            ...cloneDeep(initialRow),
            id: resp?.ID || id,
            value1: String(value),
            count: resp?.CNT || 0,
        };
    });

    report.items = [item];

    return report;
};

const mapSelfSimple = (report: Report, { ANSWER_GROUP_ID, ANSWER_ID, ANSWER_STR, ANSWER_TEXT, ID }): Row | null => {
    const row = report.question.rows.find(({ id }) => id === ANSWER_ID);
    const col = report.question.cols.find(({ id }) => id === ANSWER_GROUP_ID);
    const singleValue = ANSWER_TEXT || ANSWER_STR;
    if (singleValue) {
        return {
            ...cloneDeep(initialRow),
            id: ID,
            value1: row ? String(row.value || '') : '',
            value2: col ? String(col.name || '') : '',
            value3: ANSWER_ID,
            singleValue,
        };
    }
    return null;
};

const simpleStat = (report: Report, { resp, stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: report.id,
    };

    item.rows = report.question.rows.map(({ id, value }, idx) => {
        const response = responses.find(({ ANSWER_ID }: any) => ANSWER_ID === id);

        return {
            ...cloneDeep(initialRow),
            id,
            count: response?.CNT || 0,
            value1: String(value || idx + 1),
        };
    });

    if (resp?.responses) {
        // skip updates
        for (const [id, r] of Object.entries(resp.responses)) {
            if (Array.isArray(r)) {
                item.self.push({
                    ...cloneDeep(initialRow),
                    id: Number(String(id)),
                    values: r.map(mapSelfSimple.bind(null, report)).filter((_) => !!_),
                });
            } else {
                const answer = mapSelfSimple(report, r as any);
                if (answer) {
                    item.self.push(answer);
                }
            }
        }
    }

    report.items = [item];

    return report;
};

const selectImage = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = report.question.rows.map(({ filePath, id, value }, idx) => {
        const response = responses.find(({ ANSWER_ID }: any) => ANSWER_ID === id);

        return {
            ...cloneDeep(initialRow),
            id,
            count: response?.CNT || 0,
            value1: String(value || idx + 1),
            value2: filePath,
        };
    });

    report.items = [item];

    return report;
};

const maxDiff = (
    report: Report,
    {
        stat: {
            responses: { maxdiff_results },
        },
    }: any,
): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = maxdiff_results.map(({ answerId, name, appears, best, worst, bestWorstDiff, score }) => {
        return {
            ...cloneDeep(initialRow),
            id: answerId,
            value1: name,
            maxDiffAppears: appears,
            maxDiffBest: best,
            maxDiffWorst: worst,
            maxDiffBestWorstDiff: bestWorstDiff,
            maxDiffScore: score,
        };
    });

    report.items = [item];

    return report;
};

const number = (report: Report, { resp, stat }: any): Report => {
    const responses = resp?.responses || [];
    const statData = stat?.responses?.stat;

    const item: Item = {
        ...cloneDeep(initialItem),
        id: report.id,
    };

    item.self = responses.map(({ ANSWER_INT, ID }: any) => ({
        ...cloneDeep(initialRow),
        id: ID,
        singleValue: ANSWER_INT,
        value1: report.id,
    }));

    item.cols = [];
    let index = 0;

    // TODO workaround, remove
    if (statData && !Array.isArray(statData)) {
        for (const [key, val] of Object.entries(statData)) {
            item.cols = [
                ...item.cols,
                {
                    ...cloneDeep(initialCol),
                    id: ++index,
                    headValue: `${key}|${val}`,
                },
            ];
        }
    }

    report.items = [item];

    return report;
};

const matrixAnswers = (report: Report, { resp, stat }: any): Report => {
    if (!resp && !stat) {
        return report;
    }

    const item: Item = {
        ...cloneDeep(initialItem),
        rows: report.items.length !== 0 ? report.items[0].rows : [],
        id: report.id,
    };

    item.cols = report.question.cols.map(({ id, name }) => ({
        ...cloneDeep(initialCol),
        id,
        headValue: name,
    }));

    if (stat) {
        item.rows = report.question.rows.map(({ id, value }) => {
            return {
                ...cloneDeep(initialRow),
                id,
                value1: String(value || ''),
                cells: item.cols.map((col) => {
                    const response = stat.responses.find(
                        ({ ANSWER_GROUP_ID, ANSWER_ID }: any) => ANSWER_ID === id && col.id === ANSWER_GROUP_ID,
                    );

                    return {
                        ...cloneDeep(initialCell),
                        id: col.id,
                        str: [col.headValue, value].join(' - '),
                        count: response?.CNT || 0,
                    };
                }),
            };
        });
    }

    if (resp?.responses) {
        // skip on update
        // matrix self-answers
        const respRows: Array<Row> = [];
        for (const [key, responses] of Object.entries(resp.responses)) {
            const respRow: Row = {
                ...cloneDeep(initialRow),
                id: Number(String(key)),
            };
            (responses as Array<any>).forEach(({ ANSWER_GROUP_ID, ANSWER_ID, ANSWER_STR }: any) => {
                const row = report.question.rows.find(({ id }) => id === ANSWER_ID);
                const col = report.question.cols.find(({ id }) => id === ANSWER_GROUP_ID);

                respRow.cells.push({
                    ...cloneDeep(initialCell),
                    id: (ANSWER_ID + ANSWER_GROUP_ID) as number,
                    value1: row ? String(row.value || '') : '',
                    value2: col ? String(col.name || '') : '',
                    singleValue: ANSWER_STR,
                });
            });
            respRows.push(respRow);
        }
        item.self = respRows;
    }

    report.items = [item];

    return report;
};

const rank = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.cols = report.question.rows.map((row, index) => ({
        ...cloneDeep(initialCol),
        id: index + 1,
        headValue: String(index + 1),
    }));

    item.rows = report.question.rows.map(({ id, value }) => ({
        ...cloneDeep(initialRow),
        id,
        value1: String(value || ''),
        cells: item.cols.map(({ headValue, id: colId }) => {
            const response = responses.find(
                ({ ANSWER_ID, ANSWER_INT }: any) => ANSWER_ID === id && ANSWER_INT === colId,
            );
            return {
                ...cloneDeep(initialCell),
                id: colId,
                str: [headValue, value].join(' - '),
                count: response?.CNT || 0,
            };
        }),
    }));

    report.items = [item];

    return report;
};

const distributeScale = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = report.question.rows.map(({ id, value }) => {
        const response = responses.find(({ ANSWER_ID }: any) => ANSWER_ID === id);

        return {
            ...cloneDeep(initialRow),
            id,
            value1: String(value || ''),
            count: Number(response?.SUMMA || 0),
        };
    });

    report.items = [item];

    return report;
};

const matrixRating = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: report.question.id,
        matrixType: report.question.params.matrixType,
    };

    item.cols = [];

    for (let i = report.question.params.minAmount ?? 1; i < report.question.params.amount + 1; i++) {
        item.cols.push({
            ...cloneDeep(initialCol),
            id: i,
            headValue: String(i),
        });
    }

    item.rows = report.question.rows.map(({ id, value }) => ({
        ...cloneDeep(initialRow),
        id,
        value1: String(value || ''),
        cells: item.cols.map(({ headValue, id: colId }) => {
            const response = responses.find(
                ({ ANSWER_ID, ANSWER_INT }: any) => id === ANSWER_ID && colId === ANSWER_INT,
            );
            return {
                ...cloneDeep(initialCell),
                id: colId,
                str: [headValue, value].join(' - '),
                count: response?.CNT || 0,
            };
        }),
    }));

    report.items = [item];

    return report;
};

const csi = (report: Report, { stat: { responses, addInfo, index } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: report.question.id,
        matrixType: report.question.params.matrixType,
    };

    item.cols = [];

    for (let i = 0; i < report.question.params.amount; i++) {
        item.cols.push({
            ...cloneDeep(initialCol),
            id: i + 1,
            headValue: String(i + 1),
        });
    }

    item.rows = report.question.rows.map(({ id, value }) => {
        const response = responses.find(({ ANSWER_ID }: any) => id === ANSWER_ID);
        return {
            ...cloneDeep(initialRow),
            id,
            value1: String(value || ''),
            importance: response?.value || 0,
        };
    });
    report.chart = addInfo.map(({ START_DATE, index }: any): any => ({
        date: START_DATE,
        score: index,
    }));

    report.items = [item];

    report.addInfo = { index };

    return report;
};

const star = (report: Report, { stat: { responses, total } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = [];

    report.totalCount = total;

    for (let i = 0; i < report.question.params.amount; i++) {
        const response = responses.find(({ ANSWER_INT }: any) => i + 1 === ANSWER_INT);
        item.rows = [
            ...item.rows,
            {
                ...cloneDeep(initialRow),
                id: i + 1,
                value1: String(i + 1),
                count: response?.CNT || 0,
            },
        ];
    }

    item.rows = item.rows.reverse();

    report.items = [item];

    return report;
};

const residence = (report: Report, { stat: { responses } }: any): Report => {
    report.items = [];

    for (const [key, response] of Object.entries(responses)) {
        const { ANSWER_ID } = response[0] || {}; // TODO WTF no answers yet
        report.items.push({
            ...cloneDeep(initialItem),
            id: ANSWER_ID,
            value: RESIDENCE_TYPE_LABEL[getValue(key, 0)],
            rows: (response as Array<any>).map(({ ANSWER_ID, ANSWER_STR, ANSWER_TEXT, CNT, NAME }: any) => ({
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                count: CNT,
                value1: ANSWER_TEXT || NAME,
                value2: ANSWER_STR,
            })),
        });
    }

    return report;
};

const nestedList = (report: Report, { stat: { responses } }: any): Report => {
    report.items = [];

    for (const [key, response] of Object.entries(responses)) {
        const { ANSWER_ID } = response[0] || {}; // TODO WTF no answers yet

        report.items.push({
            ...cloneDeep(initialItem),
            id: ANSWER_ID,
            value: RESIDENCE_TYPE_LABEL[getValue(key, 0)],
            rows: (response as Array<any>).map(({ ANSWER_ID, ANSWER_STR, ANSWER_TEXT, CNT }: any) => {
                return {
                    ...cloneDeep(initialRow),
                    id: ANSWER_ID,
                    count: CNT,
                    value1: ANSWER_TEXT || '',
                    value2: parseInt(ANSWER_STR, 10), // nested item id,
                    value3: (() => {
                        try {
                            return parseJSON(key)[0] || null;
                        } catch (_) {
                            return null;
                        }
                    })(),
                };
            }),
        });
    }

    return report;
};

const semDiff = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.cols = report.question.cols.map(({ id, name }) => ({
        ...cloneDeep(initialCol),
        id,
        headValue: name,
    }));

    item.rows = report.question.rows.map(({ id, value, value2, value3 }) => ({
        ...cloneDeep(initialRow),
        id: id,
        value1: String(value || ''),
        value2: String(value2 || ''),
        value3: String(value3 || ''),
        cells: item.cols.map((col) => {
            const response = responses.find(
                ({ ANSWER_GROUP_ID, ANSWER_ID }: any) => ANSWER_ID === id && col.id === ANSWER_GROUP_ID,
            );
            return {
                ...cloneDeep(initialCell),
                id: col.id,
                str: [col.headValue, value, value2, value3].join(' - '),
                count: response?.CNT || 0,
            };
        }),
    }));

    report.items = [item];

    return report;
};

export interface NpsChartResponse {
    CRITICALS: number;
    PROMOUTERS: number;
    SCORE: number;
    START_DATE: string;
    TOTAL_CNT: number;
}

export interface NpsChart {
    critics: number;
    promoters: number;
    score: number;
    date: string;
    count: number;
}

const fileUpload = (report: Report, { stat: { addInfo } }: any): Report => {
    report.addInfo = {
        totalSize: addInfo.TOTAL_SIZE ? Number(addInfo.TOTAL_SIZE) : 0,
        totalCount: addInfo.TOTAL_CNT,
    };
    return report;
};

const nps = (report: Report, { stat: { addInfo, responses, total } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    report.totalCount = total;
    report.chart = addInfo.map(
        ({ CRITICALS, PROMOUTERS, SCORE, START_DATE, TOTAL_CNT }: NpsChartResponse): NpsChart => ({
            critics: CRITICALS,
            promoters: PROMOUTERS,
            score: SCORE,
            date: START_DATE,
            count: TOTAL_CNT,
        }),
    );

    for (let i = 0; i <= 10; i++) {
        const response = responses.find(({ ANSWER_INT }: any) => ANSWER_INT === i);
        const count = response?.CNT || 0;
        let type: NpsType = null;

        if (i <= 6) {
            type = NpsType.BAD;
        } else if (i > 6 && i <= 8) {
            type = NpsType.NORMAL;
        } else if (i > 8 && i <= 10) {
            type = NpsType.GOOD;
        }

        item.rows = [
            ...item.rows,
            {
                ...cloneDeep(initialRow),
                id: i,
                value1: String(i),
                count,
                npsType: type,
            },
        ];
    }

    let countBad = 0;
    let countNormal = 0;
    let countGood = 0;

    for (const { count, npsType } of item.rows) {
        if (npsType === NpsType.BAD) {
            countBad += count;
        } else if (npsType === NpsType.NORMAL) {
            countNormal += count;
        } else if (npsType === NpsType.GOOD) {
            countGood += count;
        }
    }

    item.cols = [
        {
            ...cloneDeep(initialCol),
            id: 1,
            count: countBad,
            npsType: NpsType.BAD,
        },
        {
            ...cloneDeep(initialCol),
            id: 2,
            count: countNormal,
            npsType: NpsType.NORMAL,
        },
        {
            ...cloneDeep(initialCol),
            id: 3,
            count: countGood,
            npsType: NpsType.GOOD,
        },
    ];

    report.items = [item];

    return report;
};

const scale = (report: Report, { stat: { responses } }: any): Report => {
    const { cols, rows } = report.question;

    const answerId = rows[0].id;

    const item: Item = {
        ...cloneDeep(initialItem),
        id: answerId,
    };

    item.rows = cols.map(({ id: groupId, name }: any) => {
        const stat = responses.find(({ ANSWER_GROUP_ID }: { ANSWER_GROUP_ID: number }) => groupId === ANSWER_GROUP_ID);

        return {
            ...cloneDeep(initialRow),
            id: groupId,
            count: stat?.CNT || 0,
            value1: name,
        };
    });

    report.items = [item];

    return report;
};

const phone = (report: Report, { resp }: any): Report => {
    if (!resp) {
        return null;
    }

    const { responses } = resp;

    report.items = [];

    for (const [key, response] of Object.entries(responses)) {
        report.items.push({
            ...cloneDeep(initialItem),
            id: Number(key),
            rows: (response as Array<any>).map(({ ANSWER_ID, ANSWER_STR, ANSWER_TEXT }: any) => {
                const row = report.question.rows.find(({ id }) => id === ANSWER_ID);
                const number = ANSWER_STR || '';
                const prefix = ANSWER_TEXT || '';

                return {
                    ...cloneDeep(initialRow),
                    id: ANSWER_ID,
                    value1: row?.value || '',
                    singleValue: prefix + number,
                };
            }),
        });
    }

    return report;
};

const name = (report: Report, { resp }: any): Report => {
    if (!resp) {
        return null;
    }

    const { responses } = resp;

    report.items = [];

    for (const [key, response] of Object.entries(responses)) {
        report.items.push({
            ...cloneDeep(initialItem),
            id: Number(key),
            rows: report.question.rows.map(({ id, value }) => ({
                ...cloneDeep(initialRow),
                id,
                value1: String(value || ''),
                cells: report.question.cols.map((col) => {
                    const cell = (response as Array<any>).find(
                        ({ ANSWER_GROUP_ID, ANSWER_ID }: any) => ANSWER_ID === id && ANSWER_GROUP_ID === col.id,
                    );
                    return {
                        ...cloneDeep(initialCell),
                        str: cell?.ANSWER_STR || '',
                    };
                }),
            })),
            cols: report.question.cols.map(({ id, name }) => ({
                ...cloneDeep(initialCol),
                id,
                headValue: name,
            })),
        });
    }

    return report;
};

const simpleList = (report: Report, { resp }: any): Report => {
    if (!resp) {
        return null;
    }

    const { responses } = resp;

    report.items = [];

    for (const [key, response] of Object.entries(responses)) {
        report.items.push({
            ...cloneDeep(initialItem),
            id: Number(key),
            rows: report.question.rows.map(({ id, value }) => {
                const cell = (response as Array<any>).find(({ ANSWER_ID }: any) => ANSWER_ID === id);

                let str = cell?.ANSWER_STR || '';

                if (str === String(CANTRATE_ID)) {
                    str = 'Нет данных';
                }

                return {
                    ...cloneDeep(initialRow),
                    id,
                    value1: String(value || ''),
                    cells: [
                        {
                            ...cloneDeep(initialCell),
                            str,
                        },
                    ],
                };
            }),
        });
    }

    return report;
};

const dropdownList = (report: Report, { stat: { responses } }: any): Report => {
    const {
        question: { rows },
    } = report;

    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = rows.map(({ id, value }): Row => {
        const resp = responses.find(({ ANSWER_ID }: { ANSWER_ID: number }) => ANSWER_ID === id);

        if (resp) {
            const { ANSWER_ID, CNT } = resp;

            return {
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                count: CNT,
                value1: value,
            };
        }

        return {
            ...cloneDeep(initialRow),
            id,
            count: 0,
            value1: value,
        };
    });

    report.items = [item];

    return report;
};

const matrixDropdownList = (report: Report, { resp }: any): Report => {
    if (!resp) {
        return report;
    }

    const { responses } = resp;

    report.items = [];

    for (const [key, response] of Object.entries(responses)) {
        report.items.push({
            ...cloneDeep(initialItem),
            id: Number(key),
            rows: report.question.rows.map(({ id, value, value2 }) => {
                return {
                    ...cloneDeep(initialRow),
                    id,
                    value1: String(value || ''),
                    value2: String(value2 || ''),
                    cells: report.question.cols.map((col) => {
                        const cell = (response as Array<any>).find(
                            ({ ANSWER_GROUP_ID, ANSWER_ID }: any) => ANSWER_ID === id && ANSWER_GROUP_ID === col.id,
                        );

                        let str = String(cell?.ANSWER_STR || '');

                        if (str === String(CANTRATE_ID)) {
                            str = 'Нет данных';
                        }

                        return {
                            ...cloneDeep(initialCell),
                            str,
                        };
                    }),
                };
            }),
            cols: report.question.cols.map(({ id, name }) => ({
                ...cloneDeep(initialCol),
                id,
                headValue: name,
            })),
        });
    }

    return report;
};

const textBlock = (report: Report, _data: any): Report => {
    const {
        question: {
            params: { textBlock },
        },
    } = report;

    report.items = [
        {
            ...cloneDeep(initialItem),
            id: 0,
            value: textBlock,
        },
    ];

    report.totalCount = 1;

    return report;
};

const text = (report: Report, { resp }: any): Report => {
    if (!resp) {
        return null;
    }

    const newRows = (resp.responses || []).map(({ ANSWER_STR, ANSWER_TEXT, ID }: any) => {
        let value1 = ANSWER_TEXT ? ANSWER_TEXT || '' : ANSWER_STR || '';

        if (value1 === String(CANTRATE_ID)) {
            value1 = CANTRATE_TEXT;
        }

        return {
            ...cloneDeep(initialRow),
            id: ID,
            value1,
        };
    });

    report.items = [
        {
            ...cloneDeep(initialItem),
            id: 1,
            self: newRows,
        },
    ];

    return report;
};

const textPerception = (report: Report, { stat }: any): Report => {
    if (!stat) {
        return null;
    }

    const item: Item = {
        ...cloneDeep(initialItem),
        id: 1,
        rows: [
            {
                ...initialRow,
                value1: 'by_phrases',
                cells:
                    stat.responses.by_phrases?.map(({ phrase, count }) => ({
                        value1: phrase,
                        count,
                    })) || [],
            },
            {
                ...initialRow,
                value1: 'by_words',
                cells:
                    stat.responses.by_words?.map(
                        ({ word, count, pos }: { word: string; count: number; pos: number }) => ({
                            value1: word,
                            value2: pos,
                            count,
                        }),
                    ) || [],
            },
        ],
    };

    report.items = [item];

    return report;
};

const pairComparison = (report: Report, { stat }: any): Report => {
    if (!stat) {
        return null;
    }

    const pair_results = stat.responses?.pair_results || [];
    const total_scores = stat.responses?.total_scores || {};
    const questionRows = report.question.rows;
    const rows = [];

    questionRows.forEach((row) => {
        rows.push({
            ...initialRow,
            id: row.id,
            value1: row.textBlock || row.order,
            count: total_scores[row.id],
            filePath: row.filePath,
            cells: questionRows.map((cell) => {
                const cellId = cell.id;

                if (row.id === cellId) {
                    return { value1: '-' };
                }

                let optionWins;
                let pairNumber;
                let id;
                let str;

                pair_results.find(
                    (item: {
                        option_1: string;
                        option_1_id: number;
                        option_1_wins: boolean;
                        option_2: string;
                        option_2_id: number;
                        option_2_wins: boolean;
                        pair_number: number;
                    }) => {
                        if (row.id === item.option_1_id && cellId === item.option_2_id) {
                            optionWins = item.option_1_wins;
                            pairNumber = item.pair_number;
                            id = item.option_2_id;
                            str = `${item.option_1} VS ${item.option_2}`;
                            return true;
                        }
                        if (row.id === item.option_2_id && cellId === item.option_1_id) {
                            optionWins = item.option_2_wins;
                            pairNumber = item.pair_number;
                            id = item.option_1_id;
                            str = `${item.option_2} VS ${item.option_1}`;
                            return true;
                        }
                        return false;
                    },
                );

                return { value1: optionWins, value2: pairNumber, id, str };
            }),
        });
    });

    const item: Item = {
        ...cloneDeep(initialItem),
        id: 1,
        rows,
    };

    report.items = [item];

    return report;
};

const figmaTest = (report: Report, { stat }: any): Report => {
    if (!stat) {
        return null;
    }

    const rows = [
        {
            ...cloneDeep(initialRow),
            id: 1,
            value1: 'Среднее время',
            value2: stat.avgTimeMs ? parseFloat((stat.avgTimeMs / 1000).toFixed(2)) : 0,
            count: stat.totalResponses || 0,
        },
        {
            ...cloneDeep(initialRow),
            id: 2,
            value1: 'Медиана времени',
            value2: stat.medianTimeMs ? parseFloat((stat.medianTimeMs / 1000).toFixed(2)) : 0,
            count: stat.totalResponses || 0,
        },
        {
            ...cloneDeep(initialRow),
            id: 3,
            value1: 'Успешно',
            value2: stat.successCount || 0,
            count: stat.totalResponses || 0,
            singleValue: '1',
        },
        {
            ...cloneDeep(initialRow),
            id: 4,
            value1: 'Сдались',
            value2: stat.surrenderCount || 0,
            count: stat.totalResponses || 0,
            singleValue: '0',
        },
    ];

    if (stat.hasTimer === true) {
        rows.push({
            ...cloneDeep(initialRow),
            id: 5,
            value1: 'Время истекло',
            value2: stat.timeoutCount || 0,
            count: stat.totalResponses || 0,
            singleValue: '2',
        });
    }

    const item: Item = {
        ...cloneDeep(initialItem),
        id: 1,
        rows,
    };

    report.items = [item];
    report.addInfo = {
        ...report.addInfo,
        figmaStat: stat,
    };

    return report;
};

const firstClick = (report: Report, { stat }: any): Report => {
    if (!stat) {
        return null;
    }

    const item: Item = {
        ...cloneDeep(initialItem),
        id: 1,
        rows: [
            {
                ...initialRow,
                value1: 'heatmap',
                cells:
                    stat?.responses.heatmap?.map(({ x, y, count }: { x: number; y: number; count: number }) => ({
                        value1: x,
                        value2: y,
                        count,
                    })) ?? [],
            },
            {
                ...initialRow,
                value1: 'clickData',
                cells:
                    stat?.responses.click_data?.map(
                        ({
                            answer_id,
                            click_order,
                            count,
                        }: {
                            answer_id: number;
                            click_order: number;
                            count: number;
                        }) => ({
                            value1: answer_id,
                            value2: click_order,
                            count,
                        }),
                    ) || [],
            },
            ...report.question.rows.map(({ id, value }, idx) => {
                const response = stat?.responses?.total_scores?.[id];
                const avgTime = stat?.responses?.average_time?.[id];
                return {
                    ...cloneDeep(initialRow),
                    id,
                    count: response || 0,
                    value1: String(value || idx + 1),
                    value2: avgTime ? parseFloat(Number(avgTime / 1000).toFixed(2)) : null,
                };
            }),
        ],
    };

    report.items = [item];

    return report;
};

const KANO_CATEGORIES = ['M', 'O', 'A', 'I', 'R', 'Q'];

// Map KANO function data from API to typed format
const mapKanoFeatures = (functions: Array<any>): Report['addInfo']['kanoFeatures'] => {
    if (!functions || functions.length === 0) {
        return undefined;
    }
    return functions;
};

const kano = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = report.question.rows.map(({ id, value }) => {
        const response = responses?.functions?.find(({ ANSWER_ID }: any) => id === ANSWER_ID);
        return {
            ...cloneDeep(initialRow),
            id,
            count: response?.CNT || 0,
            value1: String(value || ''),
            // Create 6 cells - one for each KANO category
            cells: KANO_CATEGORIES.map((categoryCode, idx) => ({
                ...cloneDeep(initialCell),
                id: idx,
                value: { category: categoryCode },
            })),
        };
    });

    report.items = [item];

    // Store KANO features in addInfo for use in component
    report.addInfo = {
        ...report.addInfo,
        kanoFeatures: mapKanoFeatures(responses?.functions),
    };

    return report;
};

const conjoint = (report: Report, { stat: { responses } }: any): Report => {
    const item: Item = {
        ...cloneDeep(initialItem),
        id: 0,
    };

    item.rows = responses.card_metrics
        .map((cardMetric) => {
            let card;
            report?.question?.conjointConfig?.tasks.find((task) =>
                task.cards.find((itemCard) => {
                    if (itemCard.id === cardMetric.cardId) {
                        card = { ...itemCard, taskId: task.id };
                    }
                }),
            );

            if (!card) return null;

            return {
                ...cloneDeep(initialRow),
                id: cardMetric.cardId,
                value1: striptags(card?.name),
                value2: card?.levels
                    .map((item) => {
                        return `${striptags(item.attributeName)}: ${striptags(item.levelName)}`;
                    })
                    .join(' / '),
                value3: cardMetric.shown,
                count: cardMetric.chosen,
                percent: cardMetric.percent,
                taskId: card?.taskId,
            };
        })
        .filter((item) => !!item);

    const levelMetrics = responses.level_metrics.reduce((acc, metric) => {
        if (acc[metric.attributeName]) {
            acc[metric.attributeName].cells.push(metric);
        } else {
            acc[metric.attributeName] = { cells: [metric] };
        }

        return acc;
    }, {});
    const levelMetricsArr = Object.entries(levelMetrics);

    report.items = [
        item,
        {
            ...cloneDeep(initialItem),
            id: 1,
            rows: levelMetricsArr.map(([itemKey, item]: any) => {
                return {
                    value1: striptags(itemKey),
                    cells: item.cells.map((cell) => {
                        return {
                            ...cloneDeep(initialCell),
                            str: striptags(cell.levelName),
                            count: cell.chosen || 0,
                            id: `${cell.attributeId}-${cell.levelId}`,
                            percent: cell.percent || 0,
                        };
                    }),
                };
            }),
        },
        {
            ...cloneDeep(initialItem),
            id: 2,
            rows: responses.importance.map((item) => ({ ...cloneDeep(initialRow), ...item })),
        },
    ];

    // Показатели по показам пересчитываем из card_metrics текущего периода:
    // responses.total с бэкенда не зависит от выбранного периода.
    const shownValues = (responses.card_metrics || []).map(({ shown }) => Number(shown) || 0);
    const computedTotal = shownValues.length
        ? {
              cards: shownValues.length,
              shown: shownValues.reduce((acc, value) => acc + value, 0),
              avgShown: Number((shownValues.reduce((acc, value) => acc + value, 0) / shownValues.length).toFixed(2)),
              minShown: Math.min(...shownValues),
              maxShown: Math.max(...shownValues),
          }
        : null;

    report.addInfo = {
        importanceIncomplete: responses.importance_incomplete,
        total: computedTotal ||
            responses.total || {
                cards: 0,
                shown: 0,
                avgShown: 0,
                minShown: 0,
                maxShown: 0,
            },
        cantRateByTask: responses.cant_rate_by_task,
        taskNamesById: report?.question?.conjointConfig?.tasks.reduce((acc, task) => {
            acc[task.id] = striptags(task.name);

            return acc;
        }, {}),
    };

    return report;
};

const cardSorting = (report: Report, { stat }: any): Report => {
    if (!stat) {
        return null;
    }

    const byAnswerCount = stat.responses?.by_answer_count || {};
    const byAnswer = stat.responses?.by_answer || {};
    const byCategory = stat.responses?.by_category || {};
    const questionRows = report.question.rows;
    const questionCategories = report.question.categories;

    const item1: Item = {
        ...cloneDeep(initialItem),
        id: 1,
        rows: [],
    };
    const item2: Item = {
        ...cloneDeep(initialItem),
        id: 2,
        rows: [],
    };
    const item3: Item = {
        ...cloneDeep(initialItem),
        id: 3,
        rows: [],
    };

    questionRows.forEach((row) => {
        item1.rows.push({
            ...initialRow,
            id: row.id,
            value1: row.value,
            cells: questionCategories.map((category) => {
                const value = byAnswerCount[row.value]?.[category.name] || 0;

                return {
                    ...cloneDeep(initialCell),
                    str: category.name,
                    count: value,
                    id: category.id,
                    percent: value,
                };
            }),
        });
    });

    item1.cols = report.question.categories.map(({ id, name }) => ({
        ...cloneDeep(initialCol),
        id,
        headValue: name,
    }));

    questionCategories.forEach((category) => {
        item2.rows.push({
            ...initialRow,
            id: category.id,
            value1: category.name,
            cells: questionRows.map((row) => {
                const value = byCategory[category.name]?.[row.value] || 0;

                return {
                    ...cloneDeep(initialCell),
                    str: row.value as string,
                    id: row.id,
                    percent: value,
                    count: value,
                };
            }),
        });
    });

    questionRows.forEach((row) => {
        item3.rows.push({
            ...initialRow,
            id: row.id,
            value1: row.value,
            cells: questionCategories.map((category) => {
                const value = byAnswer[row.value]?.[category.name] || 0;

                return {
                    ...cloneDeep(initialCell),
                    str: category.name,
                    count: value,
                    id: category.id,
                    percent: value,
                };
            }),
        });
    });

    report.items = [item1, item2, item3];

    return report;
};

export const mapComment = ({ COMMENT, ID }: CommentResponse): Comment => ({
    id: ID,
    comment: COMMENT,
});

export const mapReportStat = (report: Report, data: Array<any>): Report => {
    report = { ...report };
    const reportQuestion: { resp: any; stat: any } = data.find(({ resp, stat }: any) => {
        if (stat) {
            return stat.id === report.id;
        }
        return resp?.id === report.id;
    });

    if (!reportQuestion) {
        return report;
    }

    // TODO move to reducer

    if (reportQuestion.stat) {
        report.totalCount = reportQuestion.stat.total;

        // В периоде без ответов бэкенд может не вернуть responses — мапперы ждут массив/объект.
        if (reportQuestion.stat.responses === undefined || reportQuestion.stat.responses === null) {
            reportQuestion.stat.responses = [];
        }
    }

    report.comments = report.comments || [];
    report.totalCantRate = reportQuestion?.resp?.total_cant_rate || report?.totalCantRate || 0;
    report.addInfo = reportQuestion.stat?.addInfo;

    if (reportQuestion.resp) {
        if (reportQuestion.resp.comments) {
            report.comments = (reportQuestion.resp.comments || []).map(mapComment);
            report.commentsCount = reportQuestion.resp.total_comments;
        }
        if (reportQuestion.resp.responses) {
            report.responsesCount = reportQuestion.resp.total_responses;
        }
        report.order = report.question.order;
    }

    switch (report.type) {
        case QuestionType.NUMBER:
            return number(report, reportQuestion);
        case QuestionType.MATRIX_FEW_ANSWERS:
        case QuestionType.MATRIX_SINGLE_ANSWER:
            return matrixAnswers(report, reportQuestion);
        case QuestionType.MATRIX_RATING:
            return matrixRating(report, reportQuestion);
        case QuestionType.CSI:
            return csi(report, reportQuestion);
        case QuestionType.STAR:
            return star(report, reportQuestion);
        case QuestionType.RANK:
        case QuestionType.TEST_RANK:
            return rank(report, reportQuestion);
        case QuestionType.DISTRIBUTE_SCALE:
            return distributeScale(report, reportQuestion);
        case QuestionType.SMILE:
            return smile(report, reportQuestion);
        case QuestionType.NESTED_LIST:
            return nestedList(report, reportQuestion);
        case QuestionType.RESIDENCE:
            return residence(report, reportQuestion);
        case QuestionType.SEM_DIFF:
            return semDiff(report, reportQuestion);
        case QuestionType.NPS:
            return nps(report, reportQuestion);
        case QuestionType.SCALE:
            return scale(report, reportQuestion);
        case QuestionType.SELECT_FEW_IMAGE:
        case QuestionType.SELECT_ONE_IMAGE:
            return selectImage(report, reportQuestion);
        case QuestionType.AGREEMENT:
            return agreement(report, reportQuestion);
        case QuestionType.TEST_FEW_OF_LIST:
        case QuestionType.TEST_ONE_OF_LIST:
        case QuestionType.FEW_OF_LIST:
        case QuestionType.ONE_OF_LIST:
        case QuestionType.AB_TESTING:
            return simpleStat(report, reportQuestion);
        case QuestionType.PHONE:
            return phone(report, reportQuestion);
        case QuestionType.DATETIME:
        case QuestionType.GROUP_FREE_ANSWERS:
        case QuestionType.EMAIL:
            return simpleList(report, reportQuestion);
        case QuestionType.DROPDOWN_LIST:
            return dropdownList(report, reportQuestion);
        case QuestionType.NAME:
            return name(report, reportQuestion);
        case QuestionType.MATRIX_DROPDOWN_LIST:
        case QuestionType.MATRIX_TEXT_ANSWER:
            return matrixDropdownList(report, reportQuestion);
        case QuestionType.LONG_TEXT:
        case QuestionType.SHORT_TEXT:
        case QuestionType.TEST_TEXT:
            return text(report, reportQuestion);
        case QuestionType.TEXT_BLOCK:
            return textBlock(report, reportQuestion);
        case QuestionType.FILE_UPLOAD:
            return fileUpload(report, reportQuestion);
        case QuestionType.TEXT_PERCEPTION:
            return textPerception(report, reportQuestion);
        case QuestionType.PAIR_COMPARISON:
            return pairComparison(report, reportQuestion);
        case QuestionType.FIRST_CLICK:
            return firstClick(report, reportQuestion);
        case QuestionType.CARD_SORTING:
            return cardSorting(report, reportQuestion);
        case QuestionType.MAX_DIFF:
            return maxDiff(report, reportQuestion);
        case QuestionType.KANO:
            return kano(report, reportQuestion);
        case QuestionType.FIGMA_TEST:
            return figmaTest(report, reportQuestion);
        case QuestionType.CONJOINT:
            return conjoint(report, reportQuestion);
        default:
            return report;
    }
};

export interface LinkStatResponse {
    ID: number;
    total: number;
    totalCantRate?: number;
    unique: number;
    responses: Array<{
        VALUE: string;
        CNT: number;
    }>;
}

export const mapLinkStat = (
    stat: Array<LinkStatResponse>,
    globalFilters: GlobalFilter,
    list: Record<string, any>,
): ReportsState['linkStats'] => {
    const linksParamsById = list.reduce((acc: Record<string, any>, item: any) => ((acc[item.ID] = item), acc), {});
    const linkStats = new Map();

    stat.forEach(({ ID, responses, total, totalCantRate, unique }) => {
        const linkParam = linksParamsById[ID];

        linkStats.set(ID, {
            id: ID,
            totalCount: total,
            totalCantRate,
            uniqueCount: unique,
            responses: responses.map(({ CNT, VALUE }) => {
                const appliedFilter = globalFilters.linkParamsFilters.find(
                    (f) => f.paramId === ID && f.value === VALUE,
                );
                const count = Number(CNT);
                const percent = count ? ((count / total) * 100).toFixed(2) : 0;

                return {
                    value: VALUE,
                    count: CNT,
                    percent,
                    filterType: appliedFilter?.filterType || null,
                };
            }),
            title: linkParam.TITLE,
            name: linkParam.NAME,
            params: linkParam.PARAMS ? JSON.parse(linkParam.PARAMS) : {},
        });
    });

    return linkStats;
};
